/**
 * İki tonlu uyarı sesi — yeni sipariş bildirimi (`NewOrderWatcher`).
 *
 * Tarayıcı otomatik oynatma kuralı: ses, kullanıcı sayfada bir kez etkileşime geçmeden çalmaz.
 * Bu yüzden `NewOrderWatcher` kurulunca `document`'a bir kerelik `pointerdown` / `keydown`
 * dinleyicisi koyar; ilk etkileşimde `unlockAudio()` çağrılır ve dinleyici kendini kaldırır.
 * Ses düğmesi AÇIK'a çevrilince de `unlockAudio()` + `playAlert()` çağrılır ki kullanıcı
 * sesin gerçekten çaldığını duysun.
 *
 * Ses dosyası YOK — kısa iki tonlu sinyal Web Audio API ile üretilir: 880 Hz + 660 Hz, ~0.6 sn.
 * Görev "ses dosyası EKLEME" diyor.
 *
 * Test sahteleme: `vi.mock('./alert-sound')` ile `playAlert` ve `unlockAudio` sahte `vi.fn()` ile
 * değiştirilebilir. AudioContext jsdom'da tanımlı değil; sahtelenen modül gerçek yola
 * başvurmadığı için testte gerçek modülün kurulmasına gerek yoktur.
 */

let ctx: AudioContext | null = null;

/** AudioContext'i tembel kur; tarayıcı yokken (test) çağrılan yapıcı hata fırlatır. */
function getCtx(): AudioContext | null {
  if (ctx) return ctx;
  if (typeof window === 'undefined') return null;
  const Ctor = window.AudioContext;
  if (!Ctor) return null;
  try {
    ctx = new Ctor();
  } catch {
    ctx = null;
  }
  return ctx;
}

/**
 * AudioContext'i kurar ve "suspended" durumdaysa `resume()` eder. İlk kullanıcı etkileşiminden
 * sonra çağrılır — tarayıcı otomatik oynatma kuralı sesin çalmasını ancak o zaman izin verir.
 *
 * Hata olursa sessizce yutulur: sekme arka planda olabilir ya da tarayıcı AudioContext'i
 * desteklemiyor olabilir. Hiçbiri panelin çalışmasını engellememeli; sonraki etkileşimde
 * tekrar denenir.
 */
export async function unlockAudio(): Promise<void> {
  const audio = getCtx();
  if (!audio) return;
  if (audio.state === 'suspended') {
    try {
      await audio.resume();
    } catch {
      // Sessizce yut: tarayıcı reddederse sonraki tıklamada tekrar denenir.
    }
  }
}

/**
 * Ses çal. Tarayıcı kuralı yüzünden `AudioContext` "suspended" gelirse `resume()` çağrılır.
 * Hata olursa sessizce yutulur — kullanıcı ses tercihini kapatmış olabilir ya da sekme
 * arka planda olabilir; hiçbiri panelin çalışmasını engellememeli.
 */
export function playAlert(): void {
  const audio = getCtx();
  if (!audio) return;
  if (audio.state === 'suspended') {
    void audio.resume().catch(() => undefined);
  }
  const now = audio.currentTime;
  // İki ton: 880 Hz (yüksek A) + 660 Hz (E5). Her biri ~0.25 sn, arasında kısa boşluk.
  scheduleTone(audio, 880, now, 0.22);
  scheduleTone(audio, 660, now + 0.28, 0.22);
}

function scheduleTone(audio: AudioContext, freq: number, start: number, duration: number): void {
  const osc = audio.createOscillator();
  const gain = audio.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(freq, start);
  // Giriş/çıkış yumuşatma: tıklama sesi çıkmasın.
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(0.25, start + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
  osc.connect(gain).connect(audio.destination);
  osc.start(start);
  osc.stop(start + duration + 0.02);
}

/**
 * Ses tercihi (`localStorage`, 'aquabox:newOrderSound' = '1' | '0').
 * Erişim hatası olursa (gizli sekme, depolama kapalı) varsayılan AÇIK'a düşer — tercih
 * sorgulanamıyorsa gürültü çıkarmamak için AÇIK daha güvenli: kullanıcı paneli yeni açtı,
 * ses istemiyor olabilir ama AÇIK'tan kapatmak bir tık, KAPALI'dan açmak bir tık; AÇIK
 * varsayılanı kullanıcının haberi olmadan ses çalmasına izin vermez çünkü tarayıcı zaten
 * ilk tıklamayı şart koşuyor.
 */
export function loadSoundPreference(): boolean {
  try {
    const raw = window.localStorage.getItem('aquabox:newOrderSound');
    if (raw === '0') return false;
    return true;
  } catch {
    return true;
  }
}

export function saveSoundPreference(enabled: boolean): void {
  try {
    window.localStorage.setItem('aquabox:newOrderSound', enabled ? '1' : '0');
  } catch {
    // Sessizce yut: depolama kapalıysa tercih bellekte kalır ve sayfa yenilenince kaybolur.
  }
}
